import { 
  UserRoutingStrategy, 
  RoutingContext, 
  NavigationDestination,
  RoutingServiceConfig 
} from './types';
import { User } from '@/services/platformApi';
// MerchantRoutingStrategy disabled - merchants no longer routed to merchant tools
// import { MerchantRoutingStrategy } from './strategies/MerchantRoutingStrategy';
import { AdminRoutingStrategy } from './strategies/AdminRoutingStrategy';
import { DefaultRoutingStrategy } from './strategies/DefaultRoutingStrategy';
import { FallbackRoutingStrategy } from './strategies/FallbackRoutingStrategy';
import { getDefaultAuthenticatedDestination } from '@/utils/domainDetection';

/**
 * Service responsible for determining where users should be routed
 * after authentication or based on their user type
 */
export class UserRoutingService {
  private strategies: UserRoutingStrategy[] = [];
  private config: RoutingServiceConfig;

  constructor(config?: RoutingServiceConfig) {
    this.config = {
      checkNewTokens: true,
      checkOnboarding: true,
      respectReturnUrl: true,
      defaultPath: getDefaultAuthenticatedDestination(),
      ...config
    };

    // Register default strategies
    this.registerDefaultStrategies();
  }

  /**
   * Register default routing strategies
   */
  private registerDefaultStrategies(): void {
    this.registerStrategy(new AdminRoutingStrategy());
    // MerchantRoutingStrategy disabled - merchants no longer routed to merchant tools
    // Merchants access tools via sell.vaiu.app explicitly
    // this.registerStrategy(new MerchantRoutingStrategy());
    this.registerStrategy(new DefaultRoutingStrategy());
    this.registerStrategy(new FallbackRoutingStrategy());
    
    // Log the registered strategies and their priorities
    console.log('[UserRoutingService] Registered strategies in priority order:');
    this.strategies.forEach(strategy => {
      console.log(`  - ${strategy.constructor.name}: priority ${strategy.priority || 0}`);
    });
  }

  /**
   * Register a custom routing strategy
   */
  public registerStrategy(strategy: UserRoutingStrategy): void {
    this.strategies.push(strategy);
    // Sort by priority (higher priority first)
    this.strategies.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Remove a strategy by type
   */
  public removeStrategy(StrategyClass: new () => UserRoutingStrategy): void {
    this.strategies = this.strategies.filter(
      strategy => !(strategy instanceof StrategyClass)
    );
  }

  /**
   * Get the appropriate destination for a user after login
   */
  public async getPostLoginDestination(
    user: User,
    options?: Partial<RoutingContext>
  ): Promise<NavigationDestination> {
    const context: RoutingContext = {
      user,
      fromLogin: true,
      ...options
    };

    return this.getDestination(context);
  }

  /**
   * Get the appropriate destination for a user after signup
   */
  public async getPostSignupDestination(
    user: User,
    options?: Partial<RoutingContext>
  ): Promise<NavigationDestination> {
    const context: RoutingContext = {
      user,
      fromSignup: true,
      ...options
    };

    return this.getDestination(context);
  }

  /**
   * Get destination based on context
   */
  public async getDestination(context: RoutingContext): Promise<NavigationDestination> {
    // Debug logging
    console.log('[UserRoutingService] Determining destination for user:', {
      userId: context.user._id,
      roles: context.user.roles,
      hasMerchantProfile: !!context.user.otherProfiles?.merchant,
      fromLogin: context.fromLogin,
      fromSignup: context.fromSignup
    });

    // Check for return URL if configured
    if (this.config.respectReturnUrl && context.returnUrl) {
      const returnUrl = this.extractReturnUrl(context.returnUrl);
      if (returnUrl) {
        console.log('[UserRoutingService] Using return URL:', returnUrl);
        return { path: returnUrl };
      }
    }

    // Find the first strategy that can handle this user
    for (const strategy of this.strategies) {
      const canHandle = strategy.canHandle(context.user);
      console.log(`[UserRoutingService] Strategy ${strategy.constructor.name} canHandle: ${canHandle}`);
      
      if (canHandle) {
        try {
          const destination = await strategy.getDestination(context);
          console.log(`[UserRoutingService] Using ${strategy.constructor.name} for user ${context.user._id}, destination:`, destination.path);
          return destination;
        } catch (error) {
          console.error(`[UserRoutingService] Strategy ${strategy.constructor.name} failed:`, error);
          // Continue to next strategy
        }
      }
    }

    // Fallback to default path
    console.warn('[UserRoutingService] No strategy found, using default path');
    return { 
      path: this.config.defaultPath || getDefaultAuthenticatedDestination(),
      state: { fromLogin: context.fromLogin }
    };
  }

  /**
   * Extract and validate return URL
   */
  private extractReturnUrl(url: string | undefined): string | null {
    if (!url) return null;

    // Handle query parameter format (?returnUrl=/some/path)
    if (url.includes('returnUrl=')) {
      const params = new URLSearchParams(url);
      url = params.get('returnUrl') || url;
    }

    // Validate the URL is safe
    if (this.isValidReturnUrl(url)) {
      return url;
    }

    return null;
  }

  /**
   * Validate that a return URL is safe to navigate to
   */
  private isValidReturnUrl(url: string): boolean {
    // Prevent open redirects
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const parsed = new URL(url);
        return parsed.origin === window.location.origin;
      } catch {
        return false;
      }
    }

    // Allow relative paths but not protocol-relative URLs
    return url.startsWith('/') && !url.startsWith('//');
  }

  /**
   * Clear any cached routing data for a user
   */
  public clearUserRoutingCache(userId: string): void {
    // Clear any user-specific cached data
    localStorage.removeItem(`userPrefs_${userId}`);
    localStorage.removeItem(`lastRoute_${userId}`);
  }
}

// Export a singleton instance for convenience
export const userRoutingService = new UserRoutingService();
